import React, { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { segmentTrack } from 'flow/actions/segment-actions';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { MessageContent } from 'flow/components/tickets/UseAttachmentHandler';
import { TicketKey } from 'flow/types/EntityKeys';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';
import { PremadeAnswer } from 'flow/types/Chats';
import { ChannelValues } from 'flow/components/tickets/messageChannels';
import {
    selectAssignee,
    makeSelectRequiresWhatsAppTemplateReply,
} from 'flow/selectors/TicketsSelectors';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { Tab } from 'flow/components/tickets/Input/InputTabs';
import TabsV2 from 'flow/components/pbx/v2/misc/TabsV2';
import ExtraButtons from 'flow/components/tickets/Input/ExtraButtons';
import ButtonWhatsAppTemplateReply from 'flow/components/tickets/Input/ButtonWhatsAppTemplateReply';
import MultiChannelSendButton from 'flow/components/tickets/MultiChannelSendButton';

interface InputFooterV2Props {
    ticketKey: TicketKey;
    tabSelected: 'internal' | 'reply_external';
    tabSelectedIsWritable: boolean;
    canReply: boolean;
    canAddInternalNote: boolean;
    originPending: boolean;
    replyUnsupported: boolean;
    onClickTab: (tab: Tab) => void;
    messageContent: MessageContent;
    unlockButtonClicked: boolean;
    sendMessage: () => void;
    setExternalChannelSelected: (channel: ChannelValues) => void;
    onSelectPremadeAnswer?: (answer: PremadeAnswer) => void;
}

const InputFooterV2 = ({
    ticketKey,
    tabSelected,
    tabSelectedIsWritable,
    canReply,
    canAddInternalNote,
    originPending,
    replyUnsupported,
    onClickTab,
    messageContent,
    unlockButtonClicked,
    sendMessage,
    setExternalChannelSelected,
    onSelectPremadeAnswer,
}: InputFooterV2Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { openModal } = useModal();

    const assignee = useAppSelector((state) =>
        selectAssignee(state, ticketKey),
    );
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    const selectRequiresWhatsAppTemplateReply = useMemo(
        makeSelectRequiresWhatsAppTemplateReply,
        [],
    );
    const whatsAppTemplateReplyRequired = useAppSelector((state) =>
        selectRequiresWhatsAppTemplateReply(state, ticketKey),
    );

    const handleWhatsAppTemplateClickText = () => {
        openModal('whatsAppTemplate', { ticketKey });

        dispatch(
            segmentTrack(TICKET_STRINGS.WHATSAPP_TEMPLATE_TEXT, {
                component: 'Ticket footer',
            }),
        );
    };

    const sendIsEnabled = useMemo(
        () =>
            tabSelectedIsWritable &&
            (!!messageContent?.text?.trim() || !!messageContent?.attachment),
        [tabSelectedIsWritable, messageContent],
    );

    // A non-member may write an internal note but never reply to the bPart, and a
    // member without the tickets licence may do neither. Whether each tab may be
    // used is `useTicketComposerTab`'s to say. A refused tab stays visible and
    // goes disabled rather than disappearing, matching every other ticket control a
    // viewer cannot use.
    const getReplyTabTooltip = () => {
        if (replyUnsupported) {
            return i18next.t('tickets.reply.unsupported_channel');
        }
        if (originPending || canReply) return undefined;
        return i18next.t('tickets.no_permission');
    };
    const replyTabTooltip = getReplyTabTooltip();

    const channelTabs = useMemo(
        () => [
            {
                key: 'internal' as const,
                label: i18next.t('tickets.internal.note'),
                disabled: !canAddInternalNote,
                tooltip: canAddInternalNote
                    ? undefined
                    : i18next.t('tickets.no_permission'),
            },
            {
                key: 'reply_external' as const,
                label: i18next.t('tickets.reply'),
                disabled: !canReply,
                tooltip: replyTabTooltip,
            },
        ],
        [i18next, canAddInternalNote, canReply, replyTabTooltip],
    );

    return (
        <div className="flex items-center justify-between gap-x-2 pt-3">
            <div className="flex items-center gap-x-3 pe-3">
                <TabsV2
                    tabs={channelTabs}
                    selectedKey={tabSelected}
                    onClick={(tab) => onClickTab(tab as Tab)}
                />
                <ExtraButtons
                    ticketKey={ticketKey}
                    displayExternalReplyActions={
                        tabSelected === 'reply_external'
                    }
                    onSelectPremadeAnswer={onSelectPremadeAnswer}
                />
            </div>
            {tabSelected === 'reply_external' &&
            whatsAppTemplateReplyRequired ? (
                <ButtonWhatsAppTemplateReply
                    onClick={handleWhatsAppTemplateClickText}
                    disabled={
                        whatsAppTemplateReplyRequired &&
                        assignee !== ownExtensionKey &&
                        !unlockButtonClicked
                    }
                />
            ) : (
                <MultiChannelSendButton
                    onClickHandler={sendMessage}
                    multiChannelIsRespected={
                        false /*tabSelected === 'reply_external'*/
                    }
                    sendIsEnabled={sendIsEnabled}
                    setSelectedChannel={setExternalChannelSelected}
                    ticketKey={ticketKey}
                />
            )}
        </div>
    );
};

export default InputFooterV2;
